import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Ico } from './Icons';
import { API_URL } from '../constants';

function ChatPanel({
  conversacionActiva,
  mensajes,
  nuevoMensaje,
  setNuevoMensaje,
  enviandoMensaje,
  enviarMensaje,
  tomarConversacion,
  transferirConversacion,
  cerrarConversacion,
  finalizarConversacion,
  toggleIA,
  cambiandoIA,
  currentPermissions,
  emptyStateConfig,
  agenteLogueadoId,
  agenteActualId,
  agentes,
}) {
  const [composerTab, setComposerTab] = useState('reply');
  const [sugOpen, setSugOpen] = useState(true);
  const [sugFrases, setSugFrases] = useState([]);
  const [sugInput, setSugInput] = useState('');
  const [sugAdding, setSugAdding] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [showFinalizarModal, setShowFinalizarModal] = useState(false);
  const [motivoCierre, setMotivoCierre] = useState('');
  const [finalizandoChat, setFinalizandoChat] = useState(false);

  const MOTIVOS_CIERRE_VENTAS = [
    { value: 'venta_confirmada', label: '✅ Venta confirmada' },
    { value: 'sin_cobertura', label: '🚫 Sin cobertura' },
    { value: 'precio_no_sirve', label: '💰 Precio no le sirve' },
    { value: 'lo_piensa', label: '🤔 Lo piensa / necesita tiempo' },
    { value: 'no_responde', label: '📵 No responde' },
    { value: 'reagendar', label: '📅 Reagendar contacto' },
    { value: 'competencia', label: '🏢 Se fue con la competencia' },
    { value: 'no_interesado', label: '❌ No interesado' },
  ];

  const MOTIVOS_CIERRE_GENERAL = [
    { value: 'problema_resuelto', label: 'Problema resuelto' },
    { value: 'cliente_desiste', label: 'Cliente desiste' },
    { value: 'derivado', label: 'Derivado a otra área' },
    { value: 'no_responde', label: 'No responde' },
    { value: 'otro', label: 'Otro' },
  ];

  const MOTIVOS_CIERRE = conversacionActiva?.area === 'ventas'
    ? MOTIVOS_CIERRE_VENTAS
    : MOTIVOS_CIERRE_GENERAL;

  const handleFinalizarChat = async () => {
    if (!motivoCierre) return;
    setFinalizandoChat(true);
    try {
      if (finalizarConversacion) {
        await finalizarConversacion(motivoCierre);
      } else if (cerrarConversacion) {
        await cerrarConversacion();
      }
      setShowFinalizarModal(false);
      setMotivoCierre('');
    } catch (e) {
      console.error('Error finalizando:', e);
    } finally {
      setFinalizandoChat(false);
    }
  };

  const EMOJIS = ['👍', '👋', '✅', '❌', '⏳', '🔧', '📞', '💡', '🎉', '😊', '🙏', '👀', '📋', '🔄', '⚡', '🚀'];

  const fileToDataUrl = (file) => {
    return new Promise((resolve) => {
      if (!file || !file.type?.startsWith('image/')) {
        resolve(null);
        return;
      }

      const reader = new FileReader();

      reader.onload = () => {
        resolve(reader.result);
      };

      reader.onerror = () => {
        resolve(null);
      };

      reader.readAsDataURL(file);
    });
  };

  const getMediaUrl = (msg = {}) => {
    return (
      msg.media_url ||
      msg.mediaUrl ||
      msg.url_media ||
      msg.media ||
      msg.archivo_url ||
      msg.file_url ||
      msg.url_archivo ||
      msg.adjunto_url ||
      msg.url ||
      null
    );
  };

  const getFileName = (msg = {}) => {
    return (
      msg.archivo_nombre ||
      msg.file_name ||
      msg.nombre_archivo ||
      msg.nombre ||
      msg.filename ||
      'archivo'
    );
  };

  const getMimeType = (msg = {}) => {
    return (
      msg.mime_type ||
      msg.mimetype ||
      msg.tipo_mime ||
      msg.content_type ||
      ''
    );
  };

  const isImageMessage = (msg = {}) => {
    const url = getMediaUrl(msg) || '';
    const mime = getMimeType(msg);
    const tipo = String(msg.tipo || msg.tipo_mensaje || msg.message_type || '').toLowerCase();

    return (
      mime.startsWith('image/') ||
      tipo.includes('image') ||
      tipo.includes('imagen') ||
      url.startsWith('data:image/') ||
      /\.(png|jpg|jpeg|gif|webp|bmp)$/i.test(url.split('?')[0])
    );
  };

  const notificarMediaLocal = ({ file, previewUrl, backendData }) => {
    if (!conversacionActiva?.id || !file) return;

    const mensajeBackend = backendData?.mensaje || backendData?.message || {};
    const mediaUrl =
      getMediaUrl(mensajeBackend) ||
      backendData?.media_url ||
      backendData?.url ||
      backendData?.archivo_url ||
      backendData?.file_url ||
      previewUrl;

    const tempId = mensajeBackend.id || `media-local-${Date.now()}-${Math.random().toString(16).slice(2)}`;

    const mensajeLocal = {
      ...mensajeBackend,
      id: tempId,
      tempId,
      conversacion_id: conversacionActiva.id,
      rol: mensajeBackend.rol || 'agente',
      contenido:
        mensajeBackend.contenido ||
        (file.type?.startsWith('image/')
          ? `Imagen enviada: ${file.name || 'captura'}`
          : `Archivo enviado: ${file.name || 'archivo'}`),
      media_url: mediaUrl,
      archivo_url: mediaUrl,
      archivo_nombre: file.name || 'archivo',
      mime_type: file.type || '',
      tipo: file.type?.startsWith('image/') ? 'imagen' : 'archivo',
      tipo_mensaje: file.type?.startsWith('image/') ? 'imagen' : 'archivo',
      created_at: mensajeBackend.created_at || new Date().toISOString(),
      enviado_localmente: true,
    };

    window.dispatchEvent(
      new CustomEvent('weconnect:media-enviada', {
        detail: {
          conversacionId: conversacionActiva.id,
          mensaje: mensajeLocal,
        },
      })
    );
  };

  const enviarArchivo = async (file) => {
    if (!conversacionActiva?.id || !file) return;

    try {
      setUploadingFile(true);

      const previewUrl = await fileToDataUrl(file);

      const formData = new FormData();
      formData.append('file', file);

      const resp = await fetch(`${API_URL}/api/dashboard/conversacion/${conversacionActiva.id}/enviar-media`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('wc_token') || ''}`,
        },
        body: formData,
      });

      const data = await resp.json().catch(() => ({}));

      if (!resp.ok || data.success === false) {
        alert(data.error || 'Error enviando archivo');
        return;
      }

      notificarMediaLocal({
        file,
        previewUrl,
        backendData: data,
      });
    } catch (e) {
      console.error('Error enviando archivo:', e);
      alert('Error de conexión enviando archivo');
    } finally {
      setUploadingFile(false);
    }
  };

  useEffect(() => {
    const handlePaste = (e) => {
      if (!conversacionActiva) return;

      const items = e.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();

          const file = item.getAsFile();

          if (file && window.confirm('¿Enviar imagen pegada al cliente?')) {
            enviarArchivo(file);
          }

          return;
        }
      }
    };

    window.addEventListener('paste', handlePaste);

    return () => {
      window.removeEventListener('paste', handlePaste);
    };
  }, [conversacionActiva?.id]);

  const storageKey = `weconnect_sug_${agenteLogueadoId || 'default'}`;

  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);

      if (saved) {
        setSugFrases(JSON.parse(saved));
      } else {
        setSugFrases([
          'Buenos días, soy agente de WeConnect. ¿En qué puedo ayudarte?',
          'Voy a revisar tu cuenta, dame un momento.',
          'El problema fue resuelto. ¿Necesitás algo más?',
        ]);
      }
    } catch (e) {
      console.error('Error cargando frases sugeridas:', e);
    }
  }, [storageKey]);

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(sugFrases));
    } catch (e) {
      console.error('Error guardando frases sugeridas:', e);
    }
  }, [sugFrases, storageKey]);

  const agregarFrase = () => {
    const frase = sugInput.trim();
    if (!frase) return;

    setSugFrases((prev) => [...prev, frase]);
    setSugInput('');
    setSugAdding(false);
  };

  const borrarFrase = (index) => {
    setSugFrases((prev) => prev.filter((_, i) => i !== index));
  };

  const prevMsgCount = useRef(0);
  useEffect(() => {
    if (messagesEndRef.current) {
      const el = messagesEndRef.current;
      const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 150;
      const isNewMsg = mensajes.length !== prevMsgCount.current;
      const isNewConv = true;
      if (isNearBottom && isNewMsg) {
        el.scrollTop = el.scrollHeight;
      }
      prevMsgCount.current = mensajes.length;
    }
  }, [mensajes, conversacionActiva?.id]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      enviarMensaje();
    }
  }, [enviarMensaje]);

  if (!conversacionActiva) {
    return (
      <section className="chat chat-layout-fixed">
        <div className="chat-empty">
          <p className="chat-empty-icon">{emptyStateConfig.icono}</p>
          <h3 className="chat-empty-title">{emptyStateConfig.titulo}</h3>
          <p className="chat-empty-text">{emptyStateConfig.texto}</p>
        </div>
      </section>
    );
  }

  const isIAActive = conversacionActiva.ia_activa !== false;
  const isClosed = conversacionActiva.estado === 'cerrada';
  const prioridad = conversacionActiva.prioridad || 'normal';

  // ── Bloqueo: otro agente está atendiendo ──
  const otroAgenteAtiende = conversacionActiva.agente_id
    && agenteActualId
    && conversacionActiva.agente_id !== agenteActualId;
  const nombreOtroAgente = (() => {
    if (!otroAgenteAtiende || !agentes) return '';
    const ag = agentes.find((a) => a.id === conversacionActiva.agente_id);
    return ag?.nombre || 'Otro agente';
  })();
  // Bloquear composer si otro agente atiende o conversación cerrada
  const composerBloqueado = otroAgenteAtiende || isClosed;

  const badgeClass =
    prioridad === 'critica' || prioridad === 'alta'
      ? 'badge-alta'
      : 'badge-media';

  const badgeText =
    prioridad === 'critica' || prioridad === 'alta'
      ? 'Alta prioridad'
      : 'Prioridad media';

  const placeholder =
    composerTab === 'reply'
      ? 'Escribe tu mensaje...'
      : 'Nota interna (solo visible para agentes)...';

  return (
    <section className="chat chat-layout-fixed">
      <div className="chat-top-fixed">
        <div className="chat-header">
          <div className="chat-header-left">
            <span className="chat-header-name">
              {conversacionActiva.nombre_cliente || 'Cliente'}
            </span>

            <span className={`badge ${badgeClass}`}>
              {badgeText}
            </span>

            <span className={`mode-badge ${isIAActive ? 'ia' : 'human'}`} style={{ marginLeft: 4 }}>
              {isIAActive ? '⚡ IA activa' : '👤 Humano'}
            </span>
          </div>

          {!isClosed && (
            <div className="chat-header-right">
              {currentPermissions.takeConversation && (
                <button className="icon-btn" onClick={tomarConversacion} title="Tomar">
                  ✋
                </button>
              )}

              {currentPermissions.transferConversation && (
                <button className="icon-btn" onClick={transferirConversacion} title="Transferir">
                  🔄
                </button>
              )}

              {currentPermissions.toggleIA && (
                <button
                  className="icon-btn"
                  onClick={toggleIA}
                  disabled={cambiandoIA}
                  title={isIAActive ? 'Anular IA' : 'Reactivar IA'}
                >
                  {cambiandoIA ? '⏳' : isIAActive ? '⛔' : '🤖'}
                </button>
              )}

              {currentPermissions.closeConversation && (
                <button
                  className="icon-btn"
                  onClick={() => setShowFinalizarModal(true)}
                  title="Finalizar conversación"
                  style={{ color: '#f59e0b' }}
                >
                  🏁
                </button>
              )}
            </div>
          )}
        </div>

        <div className="chat-header-sub">
          ID: {conversacionActiva.id?.toString().substring(0, 8)} • {conversacionActiva.telefono || '-'}
        </div>

        {/* ── Mini-modal Finalizar conversación ── */}
        {showFinalizarModal && (
          <div style={{
            padding: 14,
            background: 'var(--bg)',
            borderBottom: '1px solid var(--border-light)',
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 10 }}>
              🏁 ¿Motivo de cierre?
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {MOTIVOS_CIERRE.map((motivo) => (
                <label key={motivo.value} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '6px 8px', borderRadius: 8, cursor: 'pointer',
                  background: motivoCierre === motivo.value ? '#3b82f622' : 'transparent',
                  border: motivoCierre === motivo.value ? '1px solid #3b82f644' : '1px solid transparent',
                  transition: 'all 0.15s',
                }}>
                  <input type="radio" name="motivo-cierre-chat" value={motivo.value}
                    checked={motivoCierre === motivo.value}
                    onChange={() => setMotivoCierre(motivo.value)}
                    style={{ accentColor: '#3b82f6' }} />
                  <span style={{ fontSize: 12, color: 'var(--text)' }}>{motivo.label}</span>
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button onClick={() => { setShowFinalizarModal(false); setMotivoCierre(''); }}
                style={{ flex: 1, padding: '8px 0', background: 'transparent',
                  border: '1px solid var(--border-light)', borderRadius: 8,
                  color: 'var(--muted)', fontSize: 12, cursor: 'pointer' }}>
                Cancelar
              </button>
              <button onClick={handleFinalizarChat}
                disabled={!motivoCierre || finalizandoChat}
                style={{ flex: 1, padding: '8px 0',
                  background: motivoCierre ? '#f59e0b' : '#f59e0b44',
                  border: 'none', borderRadius: 8,
                  color: motivoCierre ? '#000' : '#f59e0b88',
                  fontSize: 12, fontWeight: 600,
                  cursor: motivoCierre ? 'pointer' : 'not-allowed',
                  opacity: finalizandoChat ? 0.6 : 1 }}>
                {finalizandoChat ? '⏳ Finalizando...' : '🏁 Finalizar'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Banner: otro agente atiende ── */}
      {otroAgenteAtiende && (
        <div style={{
          padding: '4px 16px',
          background: '#f59e0b15',
          borderBottom: '1px solid #f59e0b33',
          color: '#f59e0b',
          fontSize: 11,
          fontWeight: 600,
          textAlign: 'center',
          letterSpacing: '0.03em',
        }}>
          🟡 {nombreOtroAgente} está atendiendo esta conversación
        </div>
      )}

      <div className="chat-center-scroll">
        <div className="flow-indicator">
          <div className="flow-step">✨ <span>IA como primera respuesta</span></div>
          <div className="flow-arrow">→</div>
          <div className="flow-step">👤 <span>Luego: Agente humano</span></div>
          <div className="flow-check">✓</div>
        </div>

        <div className="messages" ref={messagesEndRef}>
          {mensajes.length === 0 ? (
            <div className="chat-empty" style={{ minHeight: 120 }}>
              <p style={{ color: 'var(--muted)', fontSize: 12 }}>
                No hay mensajes
              </p>
            </div>
          ) : (
            mensajes.map((msg, idx) => {
              const isClient = msg.rol === 'cliente';
              const isIA = msg.rol === 'ia';
              const rowClass = isClient ? 'client' : isIA ? 'ia' : 'agent';
              const avatarBg = isClient ? 'var(--accent)' : isIA ? '#8b5cf6' : '#22c55e';
              const avatarText = isClient
                ? (conversacionActiva.nombre_cliente || 'C')[0]
                : isIA
                  ? '🤖'
                  : 'AG';

              const timeStr = msg.created_at
                ? new Date(msg.created_at).toLocaleTimeString('es-AR', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                : '-';

              let contenido = msg.contenido || msg.texto || '';
              let mediaUrl = getMediaUrl(msg);
              let fileName = getFileName(msg);

              // FIX: Detectar archivos embebidos como markdown [texto](url)
              // Webhook guarda comprobantes como "[Comprobante adjunto](https://...supabase.co/...)"
              if (!mediaUrl && contenido) {
                const mdMatch = contenido.match(/\[([^\]]*)\]\((https?:\/\/[^\s)]+)\)/);
                if (mdMatch) {
                  const mdText = mdMatch[1];
                  const mdUrl = mdMatch[2];
                  // Solo extraer si es URL de storage (Supabase, S3, etc)
                  if (mdUrl.includes('supabase.co/storage') || mdUrl.includes('/Comprobantes/') || /\.(pdf|png|jpg|jpeg|gif|webp)(\?|$)/i.test(mdUrl)) {
                    mediaUrl = mdUrl;
                    fileName = mdText || 'Archivo adjunto';
                    // Limpiar el contenido para no mostrar el markdown como texto
                    contenido = contenido.replace(mdMatch[0], '').trim();
                  }
                }
              }

              const imageMsg = isImageMessage({ ...msg, media_url: mediaUrl, archivo_nombre: fileName });

              if (contenido.includes('ESCALAR_A_AGENTE') || contenido.includes('Conversación escalada')) {
                return (
                  <div key={msg.id || idx} className="event-line">
                    <div className="event-divider" />
                    <span className="event-text">IA escaló al agente humano</span>
                    <div className="event-divider" />
                  </div>
                );
              }

              return (
                <div key={msg.id || msg.tempId || idx} className={`msg-row ${rowClass}`}>
                  {isClient && (
                    <div className="msg-avatar-chat" style={{ background: avatarBg }}>
                      {avatarText}
                    </div>
                  )}

                  <div className={`msg-bubble ${rowClass}`}>
                    {mediaUrl && imageMsg && (
                      <div style={{ marginBottom: contenido ? 8 : 0 }}>
                        <img
                          src={mediaUrl}
                          alt={fileName}
                          onClick={() => window.open(mediaUrl, '_blank')}
                          style={{
                            display: 'block',
                            maxWidth: 280,
                            maxHeight: 260,
                            borderRadius: 10,
                            cursor: 'pointer',
                            border: '1px solid rgba(255,255,255,0.12)',
                          }}
                        />
                      </div>
                    )}

                    {mediaUrl && !imageMsg && (
                      <a
                        href={mediaUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 8,
                          color: '#93c5fd',
                          textDecoration: 'none',
                          marginBottom: contenido ? 8 : 0,
                          border: '1px solid rgba(147,197,253,0.25)',
                          borderRadius: 8,
                          padding: '8px 10px',
                        }}
                      >
                        📎 {fileName}
                      </a>
                    )}

                    {contenido && <div className="msg-text">{contenido}</div>}

                    <div className="msg-meta">
                      <span className="msg-time-chat">{timeStr}</span>

                      {!isClient && (
                        <span className={`msg-role-badge ${isIA ? 'ia' : 'agent'}`}>
                          {isIA ? 'IA' : 'Agente'}
                        </span>
                      )}

                      {msg.enviado_localmente && (
                        <span className="msg-role-badge agent" title="Visible localmente en el dashboard">
                          enviado
                        </span>
                      )}
                    </div>
                  </div>

                  {!isClient && (
                    <div className="msg-avatar-chat" style={{ background: avatarBg, fontSize: 10 }}>
                      {avatarText}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {!composerBloqueado && (
        <div className="chat-bottom-fixed">
          <div className="suggestions">
            <div className="sug-header" onClick={() => setSugOpen(!sugOpen)} style={{ cursor: 'pointer' }}>
              <span style={{ fontSize: 14 }}>✨</span>
              <span className="sug-title">Respuestas sugeridas</span>
              <span style={{ fontSize: 11, color: 'var(--muted)', marginLeft: 'auto' }}>
                {sugOpen ? '▼' : '▶'} {sugFrases.length}
              </span>
            </div>

            {sugOpen && (
              <>
                <div className="sug-chips">
                  {sugFrases.map((frase, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <button
                        className="sug-chip"
                        onClick={() => setNuevoMensaje(frase)}
                        style={{ flex: 1 }}
                      >
                        {frase}
                      </button>

                      <button
                        onClick={() => borrarFrase(i)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: 'var(--muted)',
                          cursor: 'pointer',
                          fontSize: 12,
                          padding: '4px',
                          flexShrink: 0,
                        }}
                        title="Eliminar"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>

                {sugAdding ? (
                  <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                    <input
                      className="composer-field"
                      style={{ flex: 1, fontSize: 12, padding: '6px 10px' }}
                      placeholder="Escribí tu frase..."
                      value={sugInput}
                      onChange={(e) => setSugInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') agregarFrase();

                        if (e.key === 'Escape') {
                          setSugAdding(false);
                          setSugInput('');
                        }
                      }}
                      autoFocus
                    />

                    <button
                      onClick={agregarFrase}
                      style={{
                        background: 'var(--accent)',
                        border: 'none',
                        color: '#fff',
                        borderRadius: 6,
                        padding: '4px 10px',
                        fontSize: 12,
                        cursor: 'pointer',
                      }}
                    >
                      +
                    </button>

                    <button
                      onClick={() => {
                        setSugAdding(false);
                        setSugInput('');
                      }}
                      style={{
                        background: 'transparent',
                        border: '1px solid var(--border-light)',
                        color: 'var(--muted)',
                        borderRadius: 6,
                        padding: '4px 8px',
                        fontSize: 12,
                        cursor: 'pointer',
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setSugAdding(true)}
                    style={{
                      marginTop: 8,
                      background: 'transparent',
                      border: '1px dashed var(--border-light)',
                      borderRadius: 8,
                      padding: '6px 12px',
                      color: 'var(--muted)',
                      fontSize: 12,
                      cursor: 'pointer',
                      width: '100%',
                      textAlign: 'center',
                    }}
                  >
                    + Agregar frase
                  </button>
                )}
              </>
            )}
          </div>

          <div className="composer">
            <div className="composer-tabs">
              <button
                className={`composer-tab-btn ${composerTab === 'reply' ? 'active' : ''}`}
                onClick={() => setComposerTab('reply')}
              >
                Responder
              </button>

              <button
                className={`composer-tab-btn ${composerTab === 'note' ? 'active' : ''}`}
                onClick={() => setComposerTab('note')}
              >
                Nota interna
              </button>
            </div>

            {composerTab === 'note' && (
              <div
                style={{
                  padding: '4px 0 6px',
                  fontSize: 11,
                  color: 'var(--amber)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <span>⚠</span> Las notas internas no se envían al cliente próximamente
              </div>
            )}

            <input
              className="composer-field"
              placeholder={placeholder}
              value={nuevoMensaje}
              onChange={(e) => setNuevoMensaje(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={composerTab === 'note'}
            />

            <div className="composer-footer">
              <div className="composer-icons">
                <div style={{ position: 'relative' }}>
                  <button
                    className="icon-btn-sm"
                    onClick={() => setShowEmoji(!showEmoji)}
                    title="Emoji"
                  >
                    <Ico name="smile" size={18} />
                  </button>

                  {showEmoji && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: 36,
                        left: 0,
                        background: 'var(--card)',
                        border: '1px solid var(--border-light)',
                        borderRadius: 10,
                        padding: 8,
                        display: 'grid',
                        gridTemplateColumns: 'repeat(8, 1fr)',
                        gap: 2,
                        zIndex: 50,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                      }}
                    >
                      {EMOJIS.map((em) => (
                        <button
                          key={em}
                          onClick={() => {
                            setNuevoMensaje((prev) => prev + em);
                            setShowEmoji(false);
                          }}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            fontSize: 18,
                            cursor: 'pointer',
                            padding: 4,
                            borderRadius: 4,
                          }}
                        >
                          {em}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <button
                  className="icon-btn-sm"
                  onClick={() => {
                    if (fileInputRef.current) {
                      fileInputRef.current.setAttribute('accept', 'image/*,.pdf');
                      fileInputRef.current.click();
                    }
                  }}
                  disabled={uploadingFile}
                  title="Adjuntar archivo"
                >
                  <Ico name="clip" size={18} />
                </button>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,.pdf"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];

                    if (file && window.confirm(`¿Enviar "${file.name}" al cliente?`)) {
                      enviarArchivo(file);
                    }

                    e.target.value = '';
                  }}
                />

                <button
                  className="icon-btn-sm"
                  onClick={() => {
                    if (fileInputRef.current) {
                      fileInputRef.current.setAttribute('accept', 'image/*');
                      fileInputRef.current.click();
                    }
                  }}
                  disabled={uploadingFile}
                  title="Enviar imagen"
                >
                  <Ico name="image" size={18} />
                </button>

                <button
                  className="icon-btn-sm"
                  onClick={() => alert('Grabación de audio: próximamente')}
                  title="Micrófono"
                >
                  <Ico name="mic" size={18} />
                </button>
              </div>

              <div className="composer-actions">
                <span className="composer-hint">Enter para enviar</span>

                <button
                  className="send-btn"
                  onClick={enviarMensaje}
                  disabled={enviandoMensaje || composerTab === 'note'}
                  title="Enviar"
                >
                  {enviandoMensaje ? '⏳' : <Ico name="send" size={18} />}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {composerBloqueado && (
        <div className="chat-bottom-fixed">
          <div className="composer composer-closed">
            {otroAgenteAtiende
              ? `${nombreOtroAgente} está atendiendo esta conversación. Solo lectura.`
              : 'Conversación cerrada. Para escribir, reabrir o tomar una conversación activa.'}
          </div>
        </div>
      )}
    </section>
  );
}

export default React.memo(ChatPanel);



